import React from 'react';
import { Box, Card, CardContent, Typography, Stack, Link as MuiLink } from '@mui/material';
import {
  Payment as PaymentIcon,
  LocalShipping as ShippingIcon,
  Sync as SyncIcon,
  Notes as NotesIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';

interface EntryGroup {
  timestamp: Date | null;
  label: string;
  bodyLines: string[];
}

interface KeyValue {
  key: string;
  value: string;
}

const ISO_TS = '\\d{4}-\\d{2}-\\d{2}T[\\d:.]+';
const LABELED_BRACKET = new RegExp(`^\\[([^:\\]]+):\\s*(${ISO_TS})\\]\\s*(.*)$`);
const BARE_BRACKET = new RegExp(`^\\[(${ISO_TS})\\]\\s*(.*)$`);
const STATUS_CHANGE = /^Status changed to ([a-zA-Z_]+):?\s*(.*)$/i;
const SINGLE_KV_LINE = /^([A-Z][\w %()./-]{1,40}):\s*(.+)$/;

// Notes are a single text blob assembled by many different backend call
// sites — some put "[Label: timestamp] detail" on one line, others put the
// bracket on its own line followed by several "Key: value" lines. Group
// consecutive lines under whichever bracket header preceded them so both
// styles render the same way.
function parseNotes(raw: string): EntryGroup[] {
  const lines = raw
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);

  const groups: EntryGroup[] = [];
  let current: EntryGroup | null = null;

  for (const line of lines) {
    let m = line.match(LABELED_BRACKET);
    if (m) {
      const ts = new Date(m[2]);
      current = { timestamp: isNaN(ts.getTime()) ? null : ts, label: m[1].trim(), bodyLines: [] };
      if (m[3].trim()) current.bodyLines.push(m[3].trim());
      groups.push(current);
      continue;
    }

    m = line.match(BARE_BRACKET);
    if (m) {
      const ts = new Date(m[1]);
      const rest = m[2].trim();
      const statusMatch = rest.match(STATUS_CHANGE);
      current = statusMatch
        ? {
            timestamp: isNaN(ts.getTime()) ? null : ts,
            label: `Status → ${statusMatch[1]}`,
            bodyLines: statusMatch[2].trim() ? [statusMatch[2].trim()] : [],
          }
        : {
            timestamp: isNaN(ts.getTime()) ? null : ts,
            label: 'Update',
            bodyLines: rest ? [rest] : [],
          };
      groups.push(current);
      continue;
    }

    // Continuation line: belongs to whichever bracket entry is open;
    // otherwise it's a free-standing manual note.
    if (current) {
      current.bodyLines.push(line);
    } else {
      groups.push({ timestamp: null, label: '', bodyLines: [line] });
    }
  }

  return groups;
}

// Heuristic: break a run-on "Key: value Key2: value2" string into rows when it
// looks like several labeled fields were appended without their own newlines.
function splitKeyValues(text: string): KeyValue[] | null {
  const regex = /([A-Z][A-Za-z0-9 %()]{1,40}):\s*/g;
  const matches = [...text.matchAll(regex)];
  if (matches.length < 2) return null;
  const result: KeyValue[] = [];
  for (let i = 0; i < matches.length; i++) {
    const start = (matches[i].index ?? 0) + matches[i][0].length;
    const end = i + 1 < matches.length ? matches[i + 1].index ?? text.length : text.length;
    const value = text.slice(start, end).trim();
    if (value) result.push({ key: matches[i][1].trim(), value });
  }
  return result.length >= 2 ? result : null;
}

function getKeyValueRows(bodyLines: string[]): KeyValue[] | null {
  if (bodyLines.length === 0) return null;
  if (bodyLines.length > 1) {
    const perLine = bodyLines.map((l) => l.match(SINGLE_KV_LINE));
    if (perLine.every((m) => m)) {
      return perLine.map((m) => ({ key: m![1].trim(), value: m![2].trim() }));
    }
    return null;
  }
  return splitKeyValues(bodyLines[0]);
}

function renderValue(value: string): React.ReactNode {
  if (/^https?:\/\//i.test(value)) {
    return (
      <MuiLink href={value} target="_blank" rel="noopener noreferrer" sx={{ wordBreak: 'break-all' }}>
        {value}
      </MuiLink>
    );
  }
  return value;
}

function iconAndColor(label: string): { Icon: typeof PaymentIcon; color: 'success' | 'primary' | 'info' | 'grey' } {
  const l = label.toLowerCase();
  if (l.includes('payment')) return { Icon: PaymentIcon, color: 'success' };
  if (l.includes('status')) return { Icon: SyncIcon, color: 'primary' };
  if (l.includes('ship') || l.includes('deliver')) return { Icon: ShippingIcon, color: 'info' };
  if (l.includes('verif') || l.includes('complet')) return { Icon: CheckCircleIcon, color: 'success' };
  return { Icon: NotesIcon, color: 'grey' };
}

const EntryBody: React.FC<{ bodyLines: string[] }> = ({ bodyLines }) => {
  const kv = getKeyValueRows(bodyLines);
  if (kv) {
    return (
      <Box sx={{ mt: 0.5 }}>
        {kv.map((row, j) => (
          <Stack key={j} direction="row" spacing={1} sx={{ py: 0.25 }}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 150, flexShrink: 0 }}>
              {row.key}
            </Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
              {renderValue(row.value)}
            </Typography>
          </Stack>
        ))}
      </Box>
    );
  }
  if (bodyLines.length === 0) return null;
  return (
    <Box sx={{ mt: 0.25 }}>
      {bodyLines.map((line, j) => (
        <Typography key={j} variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
          {line}
        </Typography>
      ))}
    </Box>
  );
};

const DocumentNotesTimeline: React.FC<{ notes?: string | null }> = ({ notes }) => {
  if (!notes || !notes.trim()) return null;

  const groups = parseNotes(notes);
  const general = groups.filter((g) => !g.timestamp);
  const timed = groups
    .filter((g): g is EntryGroup & { timestamp: Date } => g.timestamp !== null)
    .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

  return (
    <Card sx={{ mt: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Notes
        </Typography>

        {general.length > 0 && (
          <Box sx={{ mb: timed.length ? 2.5 : 0 }}>
            {general.map((g, i) => (
              <Box key={i} sx={{ mb: i === general.length - 1 ? 0 : 1 }}>
                <EntryBody bodyLines={g.bodyLines} />
              </Box>
            ))}
          </Box>
        )}

        {timed.length > 0 && (
          <Stack spacing={0}>
            {timed.map((g, i) => {
              const { Icon, color } = iconAndColor(g.label);
              const isLast = i === timed.length - 1;
              return (
                <Box key={i} sx={{ display: 'flex', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        bgcolor: color === 'grey' ? 'grey.200' : `${color}.light`,
                        color: color === 'grey' ? 'grey.700' : `${color}.dark`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Icon sx={{ fontSize: 16 }} />
                    </Box>
                    {!isLast && <Box sx={{ width: 2, flexGrow: 1, minHeight: 16, bgcolor: 'divider', my: 0.5 }} />}
                  </Box>
                  <Box sx={{ pb: isLast ? 0 : 2, minWidth: 0, flex: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
                      {g.label && (
                        <Typography variant="body2" fontWeight={600}>
                          {g.label}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {format(g.timestamp, 'dd MMM yyyy, HH:mm')}
                      </Typography>
                    </Stack>
                    <EntryBody bodyLines={g.bodyLines} />
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default DocumentNotesTimeline;
